import DOMPurify from "dompurify";
import { FileText, Hash, Video, X } from "lucide-react";
import { marked } from "marked";
import { useMemo } from "react";
import { stripBroadcastAttempts } from "../../../shared/broadcasts";
import { eventCountdownLabel } from "../../../shared/event-time";
import { type MrkdwnMode, renderMrkdwn } from "../../../shared/mrkdwn";
import type { ComposerDraft, DraftDocument, MessageSection, PingKind } from "../../../shared/types";
import { Avatar } from "../../components/primitives";
import { emojiUrlResolver, useEmojiCatalog } from "../../emoji";
import { standardEmojiByName } from "../../standard-emoji";
import { timezoneEventSummary } from "./ActionsEditor";

function stripBroadcastsFromPreviewHtml(html: string) {
  const template = document.createElement("template");
  template.innerHTML = html;
  for (const token of template.content.querySelectorAll("[data-ping]")) token.remove();
  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    node.textContent = stripBroadcastAttempts(node.textContent ?? "");
    node = walker.nextNode();
  }
  return template.innerHTML;
}

function PreviewText({ html, noBroadcast = false }: { html: string; noBroadcast?: boolean }) {
  const clean = useMemo(
    () =>
      DOMPurify.sanitize(noBroadcast ? stripBroadcastsFromPreviewHtml(html) : html, {
        ALLOWED_TAGS: [
          "strong",
          "b",
          "em",
          "i",
          "s",
          "strike",
          "code",
          "pre",
          "blockquote",
          "a",
          "ul",
          "ol",
          "li",
          "br",
          "div",
          "p",
          "span",
          "img",
        ],
        ALLOWED_ATTR: [
          "href",
          "src",
          "alt",
          "class",
          "data-user-id",
          "data-channel-id",
          "data-group-id",
          "data-ping",
          "data-emoji-name",
        ],
      }),
    [html, noBroadcast],
  );
  // The payload is sanitized with a strict allowlist immediately above.
  // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized rich-text preview
  return <div className="preview-text" dangerouslySetInnerHTML={{ __html: clean }} />;
}

export function SlackFieldPreview({
  text,
  mode,
  ping = "none",
  noBroadcast = false,
}: {
  text: string;
  mode: MrkdwnMode;
  ping?: PingKind;
  noBroadcast?: boolean;
}) {
  const { catalog } = useEmojiCatalog();
  const html = useMemo(
    () =>
      renderMrkdwn(noBroadcast ? stripBroadcastAttempts(text) : text, {
        mode,
        ping: noBroadcast ? "none" : ping,
        emojiUrl: emojiUrlResolver(catalog),
        emojiUnicode: (name) => standardEmojiByName(name)?.unicode,
      }),
    [catalog, mode, noBroadcast, ping, text],
  );
  return <PreviewText html={html} noBroadcast={noBroadcast} />;
}

function MarkdownPreview({ text, noBroadcast = false }: { text: string; noBroadcast?: boolean }) {
  const clean = useMemo(
    () =>
      DOMPurify.sanitize(marked.parse(noBroadcast ? stripBroadcastAttempts(text) : text, { async: false }) as string),
    [text, noBroadcast],
  );
  // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized markdown preview
  return <div className="preview-text preview-markdown" dangerouslySetInnerHTML={{ __html: clean }} />;
}

function eventTimePreview(startsAt: string, timezone: string) {
  const date = new Date(startsAt);
  if (!Number.isFinite(date.getTime())) return startsAt;
  try {
    const viewerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const day = new Intl.DateTimeFormat(undefined, {
      dateStyle: "full",
      timeZone: viewerTimezone,
    }).format(date);
    const time24 = new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
      timeZone: viewerTimezone,
    }).format(date);
    const time12 = new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hourCycle: "h12",
      timeZone: viewerTimezone,
    }).format(date);
    return `${day}, ${time24} (${time12}) (${viewerTimezone})`;
  } catch {
    return `${date.toLocaleString()} · ${timezone}`;
  }
}

function VotePreview({
  section,
  ping,
  noBroadcast,
}: {
  section: Extract<MessageSection, { type: "vote" }>;
  ping: PingKind;
  noBroadcast: boolean;
}) {
  const showResults = section.showVoters || section.showCounts || section.showPercentages || section.showBars;
  return (
    <div className="preview-vote">
      <div className="preview-vote-question">
        <SlackFieldPreview text={section.question} mode="mrkdwn" ping={ping} noBroadcast={noBroadcast} />
        <small className="preview-vote-rule">
          {section.selectionMode === "single" ? "Choose one" : `Choose up to ${section.maxSelections}`}
        </small>
      </div>
      {showResults && (
        <div className="preview-vote-results">
          {section.options.map((option) => (
            <div className="preview-vote-option" key={option.id}>
              <strong>
                <SlackFieldPreview text={option.label} mode="emoji" ping={ping} noBroadcast={noBroadcast} />
              </strong>
              {option.description && (
                <small>
                  <SlackFieldPreview text={option.description} mode="mrkdwn" ping={ping} noBroadcast={noBroadcast} />
                </small>
              )}
              {section.showVoters && <span className="preview-vote-voters">No votes yet</span>}
              {(section.showBars || section.showPercentages || section.showCounts) && (
                <span className="preview-vote-tally">
                  {section.showBars && <code>░░░░░░░░░░░░</code>}
                  {section.showPercentages && <span>0%</span>}
                  {section.showCounts && <span>(0)</span>}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="preview-actions">
        {section.options.map((option) => (
          <button key={option.id} type="button">
            <SlackFieldPreview text={option.label} mode="emoji" ping={ping} noBroadcast={noBroadcast} />
          </button>
        ))}
      </div>
    </div>
  );
}

export function MessagePreview({
  draft,
  document,
  onClose,
  embedded = false,
}: {
  draft: ComposerDraft;
  document: DraftDocument;
  onClose?: () => void;
  embedded?: boolean;
}) {
  const noBroadcast = draft.ping === "none";
  const ping = draft.ping;
  const { catalog } = useEmojiCatalog();
  return (
    <aside className={`preview-pane ${embedded ? "embedded" : ""}`}>
      {!embedded && (
        <div className="preview-title">
          <div>
            <span>Message preview</span>
            <small>How it will appear in Slack</small>
          </div>
          <button type="button" className="preview-close" onClick={onClose} aria-label="Close preview">
            <X size={18} />
          </button>
        </div>
      )}
      <div className="preview-channel">
        <Hash size={15} /> {draft.channel.name}
      </div>
      <div className="slack-message">
        <Avatar name={draft.sender.selectedName} src={draft.sender.avatarUrl} size={38} />
        <div className="message-content">
          <div className="message-meta">
            <strong>{draft.sender.selectedName}</strong>
            <span className="app-badge">APP</span>
            <time>just now</time>
          </div>
          {document.sections.map((section) => {
            if (section.type === "text")
              return (
                <PreviewText
                  key={section.id}
                  html={renderMrkdwn(section.mrkdwn, {
                    ping,
                    emojiUrl: emojiUrlResolver(catalog),
                    emojiUnicode: (name) => standardEmojiByName(name)?.unicode,
                  })}
                  noBroadcast={noBroadcast}
                />
              );
            if (section.type === "image")
              return (
                <img key={section.id} className="preview-image" src={section.url} alt={section.alt || section.name} />
              );
            if (section.type === "video")
              return (
                <div key={section.id} className="preview-file">
                  <Video size={18} />
                  <span>
                    <strong>{section.name}</strong>
                    <small>Video attachment</small>
                  </span>
                </div>
              );
            if (section.type === "file")
              return (
                <div key={section.id} className="preview-file">
                  <FileText size={18} />
                  <span>
                    <strong>{section.name}</strong>
                    <small>{section.mimeType}</small>
                  </span>
                </div>
              );
            if (section.type === "heading")
              return (
                <div key={section.id} className={`preview-heading level-${section.level}`}>
                  <SlackFieldPreview text={section.text} mode="emoji" ping={ping} noBroadcast={noBroadcast} />
                </div>
              );
            if (section.type === "context")
              return (
                <div key={section.id} className="preview-context-row">
                  <SlackFieldPreview text={section.text} mode="mrkdwn" ping={ping} noBroadcast={noBroadcast} />
                </div>
              );
            if (section.type === "table")
              return (
                <div key={section.id} className="preview-table-wrap">
                  <table className="preview-table">
                    <tbody>
                      {section.rows.map((row, rowIndex) => (
                        <tr key={row.id}>
                          {row.cells.map((cell) => {
                            const content = (
                              <SlackFieldPreview
                                key={`preview_${cell.id}`}
                                text={cell.text}
                                mode="mrkdwn"
                                ping={ping}
                                noBroadcast={noBroadcast}
                              />
                            );
                            return rowIndex === 0 ? <th key={cell.id}>{content}</th> : <td key={cell.id}>{content}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            if (section.type === "divider") return <hr key={section.id} />;
            if (section.type === "markdown")
              return <MarkdownPreview key={section.id} text={section.text} noBroadcast={noBroadcast} />;
            if (section.type === "vote")
              return <VotePreview key={section.id} section={section} ping={ping} noBroadcast={noBroadcast} />;
            if ("actions" in section)
              return (
                <div key={section.id} className="preview-action-section">
                  {section.actions
                    .filter(
                      (action) =>
                        action.behavior === "rsvp" &&
                        action.rsvp &&
                        ((action.rsvp.showEventDetails && (action.rsvp.showEventTitle || action.rsvp.showEventTime)) ||
                          action.rsvp.showAttendees ||
                          action.rsvp.showCountdown),
                    )
                    .map((action) => (
                      <div className="preview-rsvp-event" key={`event_${action.id}`}>
                        {action.rsvp?.showEventDetails && action.rsvp.showEventTitle && (
                          <strong>
                            <SlackFieldPreview
                              text={action.rsvp.eventTitle}
                              mode="emoji"
                              ping={ping}
                              noBroadcast={noBroadcast}
                            />
                          </strong>
                        )}
                        {action.rsvp?.showEventDetails && action.rsvp.showEventTime && (
                          <span>
                            Your preview: {eventTimePreview(action.rsvp.eventStartsAt, action.rsvp.eventTimezone)}
                          </span>
                        )}
                        {action.rsvp?.showEventDetails &&
                          action.rsvp.showEventTime &&
                          (action.rsvp.messageTimeDisplay ?? action.rsvp.timeDisplay) === "local_and_original" && (
                            <small>
                              Original: {timezoneEventSummary(action.rsvp.eventStartsAt, action.rsvp.eventTimezone)}
                            </small>
                          )}
                        {action.rsvp?.showAttendees && <small>Going (0): No one yet</small>}
                        {action.rsvp?.showCountdown && <small>{eventCountdownLabel(action.rsvp.eventStartsAt)}</small>}
                      </div>
                    ))}
                  <div className="preview-actions">
                    {section.actions.map((action) => (
                      <button key={action.id} type="button" className={action.style}>
                        <SlackFieldPreview text={action.label} mode="emoji" ping={ping} noBroadcast={noBroadcast} />
                      </button>
                    ))}
                  </div>
                </div>
              );
            return null;
          })}
        </div>
      </div>
    </aside>
  );
}
